import React, { useEffect, useState } from 'react'
import { Progress, Radio } from 'antd'
import { DecryptedPatient, TelecomType } from '@icure/cardinal-sdk'

import { CustomModal } from '../../../common/CustomModal'
import './index.less'
import { useImportBingliFormsMutation } from '../../../../core/api/bingliApi'
import { BingliImportPhase, BingliImportProgress } from '../../../../core/api/bingliImport'

interface ModalSyncPatientsFromBingliProps {
  isVisible: boolean
  selectedPatients: DecryptedPatient[]
  onClose: () => void
}

type ImportScope = 'selected' | 'all'

// Share of the progress bar given to each phase; matching patients and importing surveys are the slow ones.
const PHASES: Record<BingliImportPhase, { label: string; start: number; span: number }> = {
  connecting: { label: 'Connecting to Bingli', start: 0, span: 5 },
  'fetching-surveys': { label: 'Fetching completed surveys', start: 5, span: 5 },
  'matching-patients': { label: 'Matching Bingli patients', start: 10, span: 30 },
  'importing-surveys': { label: 'Importing surveys', start: 40, span: 60 },
}

const progressPercent = ({ phase, done, total }: BingliImportProgress): number => {
  const { start, span } = PHASES[phase]
  return Math.round(start + (total > 0 ? (span * done) / total : 0))
}

const errorMessage = (error: unknown): string => {
  if (!error) return 'Unknown error'
  if (typeof error === 'object' && 'error' in error && typeof error.error === 'string') return error.error
  if (error instanceof Error) return error.message
  return JSON.stringify(error)
}

const emailsOf = (patients: DecryptedPatient[]): string[] =>
  patients.flatMap((p) =>
    (p.addresses ?? []).flatMap((a) => (a.telecoms ?? []).filter((t) => t.telecomType === TelecomType.Email && !!t.telecomNumber).map((t) => t.telecomNumber as string)),
  )

export const ModalSyncPatientsFromBingli = ({ onClose, selectedPatients, isVisible }: ModalSyncPatientsFromBingliProps) => {
  const [importBingliForms, { data: result, error, isError, isSuccess, isLoading, isUninitialized }] = useImportBingliFormsMutation()
  const [scope, setScope] = useState<ImportScope>(selectedPatients.length ? 'selected' : 'all')
  const [progress, setProgress] = useState<BingliImportProgress | undefined>()

  const startImport = () => {
    setProgress(undefined)
    importBingliForms({
      language: 'fr',
      ...(scope === 'selected' ? { patientIds: selectedPatients.map((p) => p.id), emails: emailsOf(selectedPatients) } : {}),
      onProgress: setProgress,
    })
  }

  useEffect(() => {
    if (isSuccess) {
      console.info('[bingli import] scope', scope, 'diagnostics', result?.diagnostics)
      result?.errors.forEach((e) => console.warn(e))
    }
    if (isError) console.error('[bingli import] failed', error)
  }, [isSuccess, isError])

  const diagnostics = result?.diagnostics
  const errors = result?.errors ?? []

  return (
    <CustomModal
      isVisible={isVisible}
      handleClose={isLoading ? undefined : onClose}
      closable={!isLoading}
      title={'Sync patients from Bingli'}
      secondaryBtnTitle={isUninitialized ? 'Cancel' : undefined}
      primaryBtnTitle={isUninitialized ? 'Start import' : 'Close'}
      handleClickPrimaryBtn={isUninitialized ? startImport : onClose}
      primaryBtnDisabled={isLoading}
    >
      <div className="modalSyncPatients">
        <div className="modalSyncPatients__sync">
          {isUninitialized && (
            <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)} className="modalSyncPatients__scope">
              <Radio value="selected" disabled={!selectedPatients.length}>
                Selected patients only ({selectedPatients.length})
              </Radio>
              <Radio value="all">All patients with a completed Bingli survey</Radio>
            </Radio.Group>
          )}
          {isLoading && (
            <div>
              <div>
                {progress ? PHASES[progress.phase].label : 'Starting'}
                {progress && progress.total > 1 ? ` (${progress.done}/${progress.total})` : ''}…
              </div>
              {progress?.waitingMs && <div>Bingli is limiting requests, retrying in {Math.ceil(progress.waitingMs / 1000)}s…</div>}
              <Progress percent={progress ? progressPercent(progress) : 0} status="active" />
            </div>
          )}
          {isError && <div>The import failed: {errorMessage(error)}</div>}
          {isSuccess && !result && <div>The import could not start: you are not fully logged in. Please log in again and retry.</div>}
          {isSuccess && diagnostics && (
            <>
              <Progress percent={100} />
              <div>
                {result.forms.length} questionnaire(s) imported. Completed Bingli surveys found: {diagnostics.surveysFetched}, processed for{' '}
                {scope === 'selected' ? 'the selected patients' : 'all patients'}: {diagnostics.surveysProcessed}.
              </div>
              {diagnostics.surveysProcessed === 0 && (
                <div>
                  No completed Bingli survey matched {scope === 'selected' ? 'the selected patients' : 'any patient'}. Check that the survey is completed in Bingli and that the
                  patient email is identical in Bingli and Preventi.
                </div>
              )}
              {errors.length > 0 && (
                <div>
                  The following errors have been logged:
                  {errors.map((e, index) => (
                    <p key={index}>{e}</p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </CustomModal>
  )
}
